import { Framework, Service } from '@toeverything/infra';

import type { DocService, DocsService } from '../../doc';
import { preloadDatabaseSubdocsForDataSourceRefs } from '../../doc/services/database-subdoc-preload';
import { createPublicationSettlementPort } from '../../doc/services/publication-settlement';
import type { WorkspaceService } from '../../workspace';
import {
  type AppSettingsLocation,
  appSettingsLocationTables,
  ViewConfigRowStore,
} from '../stores/view-config-row.store';
import type { AppSettingsOfflineCopyService } from './app-settings-offline-copy';

interface Entry {
  store: ViewConfigRowStore;
  references: number;
  pendingRelease: object | null;
  dispose: () => void;
}

export interface AppSettingsRowStoreLease {
  store: ViewConfigRowStore;
  release: () => void;
}

/** Shares Config reads and preload work within one host document and location. */
export class AppSettingsRowStoreService extends Service {
  private readonly entries = new Map<string, Entry>();
  private disposed = false;

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly docsService: DocsService,
    private readonly offlineCopies: AppSettingsOfflineCopyService
  ) {
    super();
  }

  acquire(
    location: AppSettingsLocation,
    docService: DocService
  ): AppSettingsRowStoreLease {
    if (this.disposed)
      throw new Error('AppSettingsRowStoreService: workspace disposed');
    const locationKey = JSON.stringify(location);
    const key = JSON.stringify([docService.doc.id, locationKey]);
    const existing = this.entries.get(key);
    const entry = existing ?? this.createEntry(location, docService);
    if (!existing) this.entries.set(key, entry);
    entry.references++;
    entry.pendingRelease = null;
    let released = false;
    return {
      store: entry.store,
      release: () => {
        if (released) return;
        released = true;
        if (this.entries.get(key) !== entry) return;
        if (--entry.references === 0) {
          void this.releaseAfterWrites(key, entry);
        }
      },
    };
  }

  override dispose(): void {
    this.disposed = true;
    // Workspace shutdown disposes immediately, even with unsettled writes.
    for (const [key, entry] of this.entries) {
      this.entries.delete(key);
      entry.dispose();
    }
    super.dispose();
  }

  private async releaseAfterWrites(key: string, entry: Entry): Promise<void> {
    const pendingRelease = {};
    entry.pendingRelease = pendingRelease;
    try {
      await entry.store.flushServerWrites();
    } catch {
      // The store already reported the write failure; still release resources.
    }
    // A new consumer cancels this release. Its later release gets its own flush,
    // so an earlier flush can never dispose the store during newer writes.
    if (
      this.entries.get(key) !== entry ||
      entry.pendingRelease !== pendingRelease ||
      entry.references !== 0
    )
      return;
    this.entries.delete(key);
    entry.dispose();
  }

  private createEntry(
    location: AppSettingsLocation,
    docService: DocService
  ): Entry {
    // loaded() acquires its own pool reference. Keep the first host's scope
    // alive even when its inspector unmounts before the other consumers.
    const host = this.docsService.loaded(docService.doc.id);
    if (!host)
      throw new Error('AppSettingsRowStoreService: host document not loaded');
    const framework = new Framework();
    framework.store(
      ViewConfigRowStore,
      () =>
        new ViewConfigRowStore(
          docService,
          location,
          createPublicationSettlementPort(this.workspaceService)
        )
    );
    const provider = framework.provider();
    let stopPreload: (() => void) | undefined;
    const dispose = () => {
      try {
        provider.dispose();
      } finally {
        try {
          stopPreload?.();
        } finally {
          host.release();
        }
      }
    };
    try {
      stopPreload = preloadDatabaseSubdocsForDataSourceRefs(
        appSettingsLocationTables(location).map(({ blockId }) => ({
          databaseId: blockId,
          blockId,
        })),
        this.workspaceService
      );
      this.offlineCopies.keep(location);
      return {
        store: provider.get(ViewConfigRowStore),
        references: 0,
        pendingRelease: null,
        dispose,
      };
    } catch (error) {
      dispose();
      throw error;
    }
  }
}
